import { useCallback, useEffect, useState, useRef } from 'react';
import { Attachment } from '@sdk/attachments/types';
import { useSession } from '@sdk/session/SessionContext';

import {
  useConversationActions,
  useConversationState,
} from '@web/conversations/Conversation/ConversationProvider/ConversationProvider';
import { usePostMessageAction } from '@web/conversations/Conversation/ConversationProvider/hooks/usePostMessageAction';
import { MessagePost } from '@web/conversations/types/Message';
import { createMessage } from '@web/conversations/utils/createMessage';
import {
  SubmitParams,
  OnContentChangeCallback,
} from '@web/richText/TextEditorInner/TextEditorInner';
import { ForwardedAttrs } from '@web/richText/TextEditor';
import { useTypingNotifier } from '@web/conversations/Conversation/ConversationTypingNotifier/conversationTypingNotifier.hooks';
import { useConversationDraftMessage } from '@web/conversations/DraftMessagesProvider/draftMessages.hooks';

import { isInactiveUser } from './conversationTextEditor.utils';

export const useConversationTextEditor = () => {
  const { currentUser } = useSession();
  const { action: postMessage } = usePostMessageAction();
  const { id, messages, interlocutor, quotedMessageId, isDisabled } = useConversationState();
  const withInactiveUser = isInactiveUser(interlocutor);
  const editorRef = useRef<ForwardedAttrs>();
  useEditorOnReply(editorRef, quotedMessageId);

  const onSubmit = useCallback(
    async ({ text, attachments, gif }: SubmitParams) => {
      const quotedMessage = messages.byId[quotedMessageId] as MessagePost;
      // @TODO needs better typing
      const newAttachements = gif
        ? [
            {
              url: gif.originalUrl,
              mimetype: 'image/gif',
            } as Attachment,
          ]
        : attachments;

      const newMessage = createMessage({
        author: currentUser,
        content: text,
        conversationId: id,
        parentMessage: quotedMessage,
        attachments: newAttachements,
      });

      await postMessage({
        conversationId: id,
        message: newMessage,
      });
    },
    [currentUser, id, messages, postMessage, quotedMessageId]
  );

  return {
    onSubmit,
    id,
    isDisabled,
    withInactiveUser,
    editorRef,
  };
};

const useEditorOnReply = (
  editorRef: React.MutableRefObject<ForwardedAttrs>,
  quotedMessageId: ID
) => {
  useEffect(() => {
    if (quotedMessageId && editorRef.current) {
      editorRef.current.focus?.();
    }
  }, [quotedMessageId, editorRef]);
};

export const useEditorActivityObserver = () => {
  const { onDraftMessageChange } = useConversationDraftMessage();
  const notify = useTypingNotifier();

  const onChange = useCallback<OnContentChangeCallback>(
    (data) => {
      notify(data);
      onDraftMessageChange(data);
    },
    [notify, onDraftMessageChange]
  );

  return {
    onChange,
  };
};
